/**
 * anlage-ballsets — die Bestaetigung vor dem einzigen schreibenden Vorgang des Programms.
 *
 * Alles andere in diesem Tool liest. Dieser Knopf nicht: nach „Jetzt anlegen" entsteht ein
 * echter Artikel in einem echten Shop, und es gibt keine Plenty-Sandbox und kein
 * Rueckgaengig. Der Dialog steht deshalb bewusst im Weg und sagt in Worten, was gleich
 * passiert — Name, Anzahl, Setgroessen, Schreibvorgaenge (T-04-13).
 *
 * DER DIALOG RUFT NICHTS AUF. Anders als sein Vorbild
 * (`vororder-management-baelle/frontend/src/components/BallAnlegenDialog.tsx`, das selbst
 * POSTet) gibt er ueber `onBestaetigt` nur die Bestaetigung zurueck; den Job startet
 * `MainPage.tsx`. Das ist dieselbe Aufteilung wie zwischen `VorschauKarten.tsx` und der
 * Seite: die Bausteine zeigen, die Seite handelt. Ein Dialog, der selbst schreibt, waere
 * eine zweite Stelle, an der ein Lauf beginnen kann — und die Sperr-Logik steht nur an
 * einer.
 *
 * „Abbrechen" ist die Standard-Aktion: Klick auf den Hintergrund und `onOpenChange(false)`
 * fuehren beide dorthin, nie zum Anlegen.
 */
import { AlertTriangle, PlusCircle, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@libero/core/components/ui/dialog';
import { zahl } from '../formate';
import type { Vorschau } from '../typen';

interface Props {
  offen: boolean;
  /** Die ANGEZEIGTE Vorschau — genau sie wird angelegt, nicht der Stand der Eingabefelder. */
  vorschau: Vorschau | null;
  /** Der Haken „Trotzdem anlegen" aus der gelben Box (EING-05). */
  trotzdem: boolean;
  onAbbruch: () => void;
  onBestaetigt: () => void;
}

/**
 * Was die KERN-ANLAGE kostet: die Plenty-Kopie des Originals, der Texte-Schritt, die
 * Hauptvariante — und je Set-Variante zwei Aufrufe, die Variante selbst und ihr Paket.
 *
 * Seit Phase 7 entsteht der Artikel als Kopie, statt Feld fuer Feld nachgebaut zu werden.
 * Bilder, Merkmale, Eigenschaften, Tags, Versandprofile, Marktplaetze und Cross-Selling
 * bringt die Kopie MIT — sie kosten keinen eigenen Schreibvorgang mehr. Aus `1 + 2 × n`
 * wurde deshalb `3 + 2 × n`: der Kopier-Aufruf, die Texte und die Hauptvariante sind ab
 * jetzt eigene Schritte.
 *
 * Bewusst hier aus der Vorschau gerechnet und nicht aus `writesGeschaetzt` gelesen: die
 * Zahl des Backends zaehlt ALLE Schritte des Schreibplans — die Kern-Anlage UND die
 * Nacharbeit (die mitkopierten Barcodes wieder entfernen). Beide stehen im Dialog
 * nebeneinander, damit der Nutzer sieht, was der Lauf anlegt und was er hinterher
 * aufraeumt.
 */
export function kernWrites(anzahlSetVarianten: number): number {
  return 3 + 2 * anzahlSetVarianten;
}

/** Eine Zeile „Bezeichnung | Wert" im Kasten. */
function Zeile({ titel, wert }: { titel: string; wert: string }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-3 py-1.5 border-b border-[#DAD9D9] last:border-0">
      <div className="text-xs font-medium text-[#1A1A1A]">{titel}</div>
      <div className="text-xs text-[#1A1A1A] break-words">{wert}</div>
    </div>
  );
}

export default function BestaetigungsDialog({
  offen, vorschau, trotzdem, onAbbruch, onBestaetigt,
}: Props) {
  // Ohne Vorschau gibt es nichts zu bestaetigen. Der Fall sollte nicht auftreten (die Seite
  // oeffnet den Dialog nur mit geladener Vorschau) — er darf aber nicht zum Absturz fuehren.
  if (!offen || !vorschau) return null;

  const setVarianten = vorschau.ziel.setVarianten ?? [];
  const setgroessen = vorschau.setKombination?.setgroessen ?? [];
  const kern = kernWrites(setVarianten.length);
  const gesamt = vorschau.writesGeschaetzt?.gesamt ?? kern;
  // Die Nacharbeit ist der Rest — beim Vorbild `as-401620` genau der eine mitkopierte
  // Barcode. `Math.max(0, …)` nur fuer den Fall, dass die Zahl aus dem Plan fehlt und
  // `gesamt` auf `kern` zurueckfaellt — eine negative Zahl waere Unsinn.
  const nacharbeit = Math.max(0, gesamt - kern);
  // REINE ANZEIGE (E73): die Zahl bremst nichts, blockiert nichts und setzt keinen Timer.
  // Gedrosselt wird im `plentyClient` — hier steht nur, womit der Nutzer rechnen muss.
  //
  // DIE DAUERZEILE ERSCHEINT NUR UEBER DEM STUNDENLIMIT. Die Formel `gesamt / 75 × 60`
  // unterstellt, dass JEDER Schreibvorgang an der Stundengrenze wartet. Das stimmt erst,
  // wenn der Lauf sie ueberhaupt reissen kann: bei acht Schreibvorgaengen stuenden dort
  // „bis zu 7 Minuten", und der Lauf ist nach Sekunden durch — eine Zahl, die niemand
  // wiedererkennt. Eine sichtbar falsche Zahl beschaedigt auch die Zahlen daneben.
  const ueberStundenlimit = gesamt > 75;
  const geschaetzteMinuten = Math.ceil((gesamt / 75) * 60);
  // Dazwischen liegt ein dritter Fall (Review IN-05): Bei 20 bis 75 Schreibvorgaengen ist
  // das Stundenkontingent zu einem guten Teil aufgebraucht, aber nicht gerissen. „Weniger
  // als eine Minute" stimmt dann nur, solange kein anderes Programm gleichzeitig schreibt —
  // ein einziger paralleler Lauf reicht, und der `plentyClient` drosselt. Genannt wird
  // deshalb die ABHAENGIGKEIT und keine Minutenzahl: eine Zahl, die von einem fremden Lauf
  // abhaengt, waere geraten.
  const nennenswerterAnteil = !ueberStundenlimit && gesamt > 20;

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) onAbbruch(); }}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#1A1A1A]">Ballset in Plenty anlegen?</DialogTitle>
        </DialogHeader>

        <div className="mt-3 rounded border border-[#DAD9D9] px-3 py-1">
          <Zeile titel="Neuer Artikel" wert={vorschau.ziel.name1} />
          <Zeile
            titel="Set-Varianten"
            wert={`${zahl(setVarianten.length)} · Setgrößen ${setgroessen.join(' + ') || '—'}${
              vorschau.setKombination?.erzwungen ? ' (erzwungen, Spielball)' : ''
            }`}
          />
          <Zeile
            titel="Schreibvorgänge"
            wert={`${zahl(gesamt)} insgesamt · ${zahl(kern)} Kern-Anlage `
              + `(Kopie, Texte, Hauptvariante, je Set-Variante Anlage und Paket)`
              + ` · ${zahl(nacharbeit)} Nacharbeit (mitkopierte Barcodes entfernen)`}
          />
          <Zeile
            titel="Original"
            wert={`${vorschau.original.variantenNr} · Artikel ${vorschau.original.itemId}`}
          />
        </div>

        <ul className="mt-3 space-y-1.5 text-xs text-gray-600 list-disc pl-4">
          <li>
            Der Artikel entsteht als <strong className="text-[#1A1A1A]">Kopie</strong> des
            Originals und bleibt <strong className="text-[#1A1A1A]">INAKTIV</strong>. Bilder,
            Merkmale, Eigenschaften, Tags, Versandprofile, Marktplätze und Cross-Selling bringt
            die Kopie mit; der Lauf ändert danach nur noch Texte, Varianten, Preise und Pakete
            — und entfernt die mitkopierten Barcodes. Vor dem Freischalten in Plenty prüfen.
          </li>
          {ueberStundenlimit ? (
            <li>
              Plenty erlaubt 75 Schreibvorgänge pro Stunde; der Client drosselt selbst — bei{' '}
              {zahl(gesamt)} Schreibvorgängen kann der Lauf bis zu ~{zahl(geschaetzteMinuten)}{' '}
              Minuten dauern. Das Fenster darf dabei offen bleiben, und ein Abbruch spart
              nichts: der angefangene Artikel bliebe stehen.
            </li>
          ) : nennenswerterAnteil ? (
            <li>
              Der Lauf braucht {zahl(gesamt)} der 75 Schreibvorgänge, die Plenty pro Stunde
              erlaubt — wie lange er dauert, hängt davon ab, wie viel vom Stundenkontingent
              noch übrig ist (schreibt ein anderes Programm gerade mit, drosselt der Client).
              Das Fenster darf dabei offen bleiben, und ein Abbruch spart nichts: der
              angefangene Artikel bliebe stehen.
            </li>
          ) : (
            <li>
              Der Lauf dauert in der Regel weniger als eine Minute. Das Fenster darf dabei
              offen bleiben, und ein Abbruch spart nichts: der angefangene Artikel bliebe
              stehen.
            </li>
          )}
          <li>
            Das Original wird nicht verändert — auch der Kopier-Aufruf liest es nur.
          </li>
        </ul>

        {trotzdem && (
          <div className="mt-3 flex items-start gap-2 rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-[#1A1A1A]">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              Zu diesem Original steht bereits ein Ballset im Protokoll
              {vorschau.bestehendesBallset
                ? ` (Artikel ${vorschau.bestehendesBallset.ballsetItemId}, Lauf #${vorschau.bestehendesBallset.laufId})`
                : ''}
              . Dieser Lauf legt ein <strong>zweites</strong> an — beide liegen danach in Plenty,
              und das überzählige muss von Hand entfernt werden.
            </span>
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onAbbruch}
            className="inline-flex items-center justify-center gap-1.5 rounded px-4 py-2 text-sm font-medium bg-[#DAD9D9] text-[#1A1A1A] hover:bg-[#c9c8c8]"
          >
            <X size={14} /> Abbrechen
          </button>
          <button
            type="button"
            onClick={onBestaetigt}
            className="inline-flex items-center justify-center gap-1.5 rounded px-4 py-2 text-sm font-medium bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            <PlusCircle size={14} /> Jetzt anlegen
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
